// ══════════════════════════════════════════════════════════════
// Concept Cards — short "teach before test" intros for each module.
//
// Shown to a child once per (skill × N days) cadence at the START of a
// session, before any practice item. The goal is the lightweight Khan
// pattern: a 1-screen reminder of what the skill is and one example,
// then straight into doing it.
//
// Each card has:
//   title:    short noun phrase (e.g. "Counting in pairs")
//   oneLine:  one sentence, child-friendly, plain words
//   demo:     1-2 example lines (e.g. "🍎🍎🍎 → 3")
//   emoji:    a single emoji for the hero spot
//
// If a module has no card here, no intro is shown (silent fallback).
// ══════════════════════════════════════════════════════════════

interface ConceptCard {
  title: string;
  oneLine: string;
  demo: string[];
  emoji: string;
}

const CONCEPT_CARDS: Record<string, ConceptCard> = {
  // ── Pre-arithmetic (age 4) ──
  "math.00a": {
    title: "Counting things",
    oneLine: "Touch each thing as you say a number out loud. The last number tells you how many.",
    demo: ["🍎 → 1", "🍎🍎 → 2", "🍎🍎🍎 → 3"],
    emoji: "🍎",
  },
  "math.00b": {
    title: "Counting up to ten",
    oneLine: "Keep going! After 5 comes 6, 7, 8, 9, 10.",
    demo: ["🐶🐶🐶🐶🐶🐶 → 6", "⭐⭐⭐⭐⭐⭐⭐ → 7"],
    emoji: "🔢",
  },
  "math.00c": {
    title: "Numerals 0–5",
    oneLine: "These are the shapes we use to write numbers. Each shape is a number you say.",
    demo: ["0 zero", "1 one", "2 two", "3 three"],
    emoji: "5️⃣",
  },
  "math.00d": {
    title: "Numerals up to 10",
    oneLine: "Numbers 6 to 10 each have their own shape too.",
    demo: ["6 six", "7 seven", "8 eight", "9 nine", "10 ten"],
    emoji: "🔟",
  },
  "math.00e": {
    title: "Writing numbers",
    oneLine: "Trace the number shape. Start at the dot and follow the path.",
    demo: ["3 — start at the top, curve right and right again"],
    emoji: "✏️",
  },
  "math.00f": {
    title: "Shapes",
    oneLine: "Shapes are around us. Look at the corners and sides.",
    demo: ["⭕ circle — round, no corners", "🟦 square — 4 same sides", "🔺 triangle — 3 sides"],
    emoji: "🔷",
  },
  "math.00g": {
    title: "Patterns",
    oneLine: "A pattern repeats. Look for what comes back again.",
    demo: ["🔴🔵🔴🔵🔴 → 🔵", "⭐🌙⭐🌙 → ⭐"],
    emoji: "🔁",
  },
  // ── Math: number sense + early ops ──
  "math.01": {
    title: "Just look — no counting",
    oneLine: "Some small groups you can see at a glance. Look at the dots and say the number fast.",
    demo: ["•• → 2", "::: → 3 (you can see it!)"],
    emoji: "👀",
  },
  "math.05": {
    title: "Two parts make a whole",
    oneLine: "Numbers can break into two parts. 5 can be 4 and 1, or 3 and 2.",
    demo: ["5 = 4 + 1", "5 = 3 + 2", "5 = 2 + 3"],
    emoji: "🧩",
  },
  "math.06": {
    title: "Make 10",
    oneLine: "Find pairs that make 10. They're useful everywhere.",
    demo: ["1 + 9 = 10", "2 + 8 = 10", "5 + 5 = 10"],
    emoji: "🔟",
  },
  "math.07": {
    title: "Adding up to 5",
    oneLine: "Putting together two small groups. Count them all to find the total.",
    demo: ["2 + 1 = 3", "1 + 4 = 5"],
    emoji: "➕",
  },
  "math.08": {
    title: "Taking away",
    oneLine: "Start with a number, take some away. What's left?",
    demo: ["5 − 2 = 3", "4 − 1 = 3"],
    emoji: "➖",
  },
  "math.09": {
    title: "Adding to 10",
    oneLine: "Try counting on from the bigger number. 7 + 2 → start at 7, count 8, 9.",
    demo: ["6 + 3 → 7, 8, 9", "5 + 4 → 6, 7, 8, 9"],
    emoji: "➕",
  },
  "math.11": {
    title: "Adding past 10",
    oneLine: "Make 10 first, then add the rest. 8 + 5 → 8 + 2 = 10, then + 3 = 13.",
    demo: ["8 + 5 = 13 (8 + 2 + 3)", "9 + 4 = 13 (9 + 1 + 3)"],
    emoji: "🌉",
  },
  // ── Reading ──
  "reading.0a": {
    title: "Rhymes",
    oneLine: "Rhyming words end with the same sound. Cat and hat both end with /at/.",
    demo: ["cat ↔ hat", "dog ↔ frog", "sun ↔ fun"],
    emoji: "🎵",
  },
  "reading.0b": {
    title: "Syllables",
    oneLine: "Words have beats. Clap once for each beat. Cat = 1 clap, apple = 2 claps.",
    demo: ["cat 👏 → 1", "ap-ple 👏👏 → 2", "el-e-phant 👏👏👏 → 3"],
    emoji: "👏",
  },
  "reading.01": {
    title: "Letter names",
    oneLine: "Every letter has a name. The first letter of the alphabet is A. Then B, C…",
    demo: ["a — say 'ay'", "b — say 'bee'", "c — say 'see'"],
    emoji: "🔤",
  },
  "reading.02": {
    title: "Letter sounds",
    oneLine: "Letters also have sounds. M says /m/ like in 'mom'. S says /s/ like in 'sun'.",
    demo: ["m → /m/ (mom)", "s → /s/ (sun)", "t → /t/ (top)"],
    emoji: "🔊",
  },
  "reading.04": {
    // MA-1065: this skill is served as sound boxes (hear each sound, put its
    // letter in a box), not oral blending, so the card says that.
    title: "Sounds to letters",
    oneLine: "Listen to each sound. Find the letter that makes it. /c/ /a/ /t/ → c a t.",
    demo: ["/m/ /a/ /p/ → m a p", "/s/ /u/ /n/ → s u n"],
    emoji: "🧩",
  },
  "reading.06": {
    title: "Reading short words",
    oneLine: "Sound out each letter. Start, middle, end. Then say the whole word.",
    demo: ["cat → /c/ /a/ /t/ → cat", "pin → /p/ /i/ /n/ → pin"],
    emoji: "📖",
  },
  "reading.09": {
    title: "Two letters at the start",
    oneLine: "Some words start with two sounds. Say both before the vowel.",
    demo: ["flag → /f/ /l/ /a/ /g/", "stop → /s/ /t/ /o/ /p/"],
    emoji: "🌊",
  },
  "reading.11": {
    title: "Letter teams",
    oneLine: "Two letters can make ONE sound. SH says /sh/. CH says /ch/. TH says /th/.",
    demo: ["ship → /sh/ /i/ /p/", "chop → /ch/ /o/ /p/"],
    emoji: "🤝",
  },
  "reading.12": {
    title: "Silent e",
    oneLine: "An 'e' at the end is silent — but it makes the vowel say its name. cap → cape.",
    demo: ["cap (short a) → cape (long a)", "kit → kite", "hop → hope"],
    emoji: "🤫",
  },
  "reading.16": {
    title: "Big school words",
    oneLine: "Some words help you learn. 'Describe' means tell what something is like. 'Compare' means find what's the same.",
    demo: ["describe → tell about it", "compare → look for same/different"],
    emoji: "🏫",
  },
};

/**
 * Decide whether to show a concept card before this session.
 * Cadence: show on first encounter with a skill, then every N days.
 */
const REFRESH_DAYS = 3;

export function shouldShowConceptCard(moduleId: string, conceptCardLog: Record<string, number> | undefined): boolean {
  if (!CONCEPT_CARDS[moduleId]) return false;
  const last = conceptCardLog?.[moduleId];
  if (!last) return true; // never shown → show
  const daysSince = (Date.now() - last) / (1000 * 60 * 60 * 24);
  return daysSince >= REFRESH_DAYS;
}

export function recordConceptCardShown(moduleId: string, conceptCardLog: Record<string, number> | undefined): Record<string, number> {
  return { ...(conceptCardLog || {}), [moduleId]: Date.now() };
}

export function getConceptCard(moduleId: string): ConceptCard | null {
  return CONCEPT_CARDS[moduleId] || null;
}

/** MA-1065 — every module id that has a concept card, so a test can iterate
 *  ALL cards (not one) without reading this file's source text. */
export function conceptCardModuleIds(): string[] {
  return Object.keys(CONCEPT_CARDS);
}
